'use client';

import { useState } from 'react';
import { cn } from '@/lib/utils/tw-merge';
import { UiIcon } from '@/components/ui/UiIcon';
import { NavLink } from '@/features/navigation/NavLink';
import type { FooterColumnDto } from '@/lib/sanity/dto';
import { FOOTER_COLUMN_TITLE_CLASS, FOOTER_LINK_CLASS } from './footer.constants';

/** Footer link column. Below `md` it collapses into a tap-to-open accordion;
 *  from `md` up it is always expanded and the toggle chevron is hidden.
 *
 *  The only interactive part of the footer, which is why it is the only piece
 *  that ships to the browser (see AGENTS.md → Performance). */
export function FooterLinkColumn({ column }: { column: FooterColumnDto }) {
    const [open, setOpen] = useState(false);

    return (
        <div className="flex flex-col">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className={cn(
                    'flex w-full items-center justify-between border-b border-hairline py-4 text-left',
                    'cursor-pointer md:mb-4 md:cursor-default md:border-0 md:py-0',
                )}
            >
                <span className={FOOTER_COLUMN_TITLE_CLASS}>{column.title}</span>
                <UiIcon
                    name="chevron-down"
                    size={16}
                    className={cn('shrink-0 transition-transform duration-200 md:hidden', open && 'rotate-180')}
                />
            </button>

            <ul
                className={cn(
                    'flex flex-col gap-2.5 overflow-hidden transition-all duration-300 ease-out',
                    open ? 'max-h-75 pt-3 pb-4 opacity-100' : 'max-h-0 opacity-0',
                    'md:max-h-none md:overflow-visible md:pt-0 md:pb-0 md:opacity-100',
                )}
            >
                {column.links.map((item) => (
                    <li key={item._key} className="leading-5">
                        <NavLink item={item} className={FOOTER_LINK_CLASS} />
                    </li>
                ))}
            </ul>
        </div>
    );
}
